import React, { useEffect, useMemo, useState } from "react";
import { FieldSelectFit } from "@/components/trade-filter-selects";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import TradingActivityDayModal from "./TradingActivityDayModal";
import {
  dashboardActivityCalendarCardClassName,
  dashboardActivityCalendarContentClassName,
  dashboardActivityCalendarHeaderClassName,
  dashboardMutedTextClassName,
} from "./dashboardChartCardShell";
import { DailyActivityResponse, Trade } from "../../../../types/trade";
import { fetchTrades } from "../../../../services/tradeApi";
import { backendTradeToFrontendTrade } from "../../../../services/tradeStorage";
import { useDisplayCurrency } from "../../../../contexts/DisplayCurrencyContext";
import { formatDisplayCurrencyCompact } from "../../../../utils/formatDisplayCurrency";
import { buildActivityMonthOptions } from "../../../../utils/activityCalendarMonths";
import {
  endOfIanaCalendarDayUtc,
  startOfIanaCalendarDayUtc,
} from "../../../../utils/notificationPeriod";
import { deviceIanaTimeZone } from "../../../../utils/reportingTimeZone";
import { pnlBarFill } from "../../Statistics/components/statsSignedPnlBarUtils";

interface TradeActivityCalendarProps {
  dailyActivityData?: DailyActivityResponse | null;
  filterPeriodLabel?: string;
  calendarMonthAnchor?: string;
  brokerAccountCode?: string;
  reportingTimeZone?: string;
}

interface DayCell {
  date: Date | null;
  dateKey: string;
  dayOfMonth: number | null;
  tradeCount: number;
  pnl: number;
}

function formatLocalYmd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAYS_SHORT = ["S", "M", "T", "W", "T", "F", "S"];

const titleClassName = cn(
  "text-sm font-medium uppercase font-[family-name:var(--font-primary)]",
  dashboardMutedTextClassName
);

const metricClassName =
  "text-sm font-medium font-[family-name:'IBM_Plex_Sans',sans-serif]";

const periodLabelClassName = cn(
  "text-sm font-normal font-[family-name:var(--font-primary)]",
  dashboardMutedTextClassName
);

const dayHeaderClassName = "rounded-md py-1 text-center sm:py-1.5 lg:py-2";

const dayCellBaseClassName =
  "flex h-[64px] min-w-0 flex-col justify-between overflow-hidden rounded-md p-1 transition-[transform,box-shadow] duration-[180ms] ease-linear sm:h-[88px] sm:p-1.5 md:h-[110px] md:p-2 lg:h-[150px] lg:p-3";

const calendarGridClassName = "grid grid-cols-7 gap-1 sm:gap-1.5 lg:grid-cols-8 lg:gap-2";

const CalendarTileIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={cn("hidden size-4 md:block lg:size-5", dashboardMutedTextClassName)}
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5m-9-6h.008v.008H12v-.008ZM12 15h.008v.008H12V15Zm0 2.25h.008v.008H12v-.008ZM9.75 15h.008v.008H9.75V15Zm0 2.25h.008v.008H9.75v-.008ZM7.5 15h.008v.008H7.5V15Zm0 2.25h.008v.008H7.5v-.008Zm6.75-4.5h.008v.008h-.008v-.008Zm0 2.25h.008v.008h-.008V15Zm0 2.25h.008v.008h-.008v-.008Zm2.25-4.5h.008v.008H16.5v-.008Zm0 2.25h.008v.008H16.5V15Z"
    />
  </svg>
);

type CalendarCellTone = "header" | "empty" | "positive" | "negative" | "summary";

function calendarDayCellTone(
  day: DayCell,
  hasData: boolean,
  positive: boolean
): CalendarCellTone {
  if (day.dayOfMonth == null || !hasData) return "empty";
  return positive ? "positive" : "negative";
}

function dayCellClassName(hasData: boolean, positive: boolean): string {
  if (!hasData) {
    return cn(dayCellBaseClassName, "bg-muted/40");
  }
  return cn(
    dayCellBaseClassName,
    "cursor-pointer hover:-translate-y-px hover:shadow-md",
    positive
      ? "bg-[#f3fff7] dark:bg-[rgba(6,227,99,0.14)]"
      : "bg-[#fff7f7] dark:bg-[rgba(224,1,1,0.12)]"
  );
}

const TradeActivityCalendar: React.FC<TradeActivityCalendarProps> = ({
  dailyActivityData,
  filterPeriodLabel,
  calendarMonthAnchor,
  brokerAccountCode,
  reportingTimeZone,
}) => {
  const effectiveTimeZone = reportingTimeZone || deviceIanaTimeZone();
  const { displayCurrency } = useDisplayCurrency();

  const activityDateKeys = useMemo(
    () => dailyActivityData?.daily_data_points?.map((p) => p.date).filter(Boolean) as string[] | undefined,
    [dailyActivityData]
  );

  const monthOptions = useMemo(
    () => buildActivityMonthOptions(activityDateKeys, effectiveTimeZone),
    [activityDateKeys, effectiveTimeZone]
  );

  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [selectedDayKey, setSelectedDayKey] = useState<string | null>(null);
  const [isDayModalOpen, setIsDayModalOpen] = useState(false);
  const [dayTrades, setDayTrades] = useState<Trade[]>([]);
  const [isDayTradesLoading, setIsDayTradesLoading] = useState(false);
  const [dayTradesError, setDayTradesError] = useState<string | null>(null);

  useEffect(() => {
    if (!calendarMonthAnchor || !/^\d{4}-\d{2}$/.test(calendarMonthAnchor)) return;
    setSelectedMonth(calendarMonthAnchor);
  }, [calendarMonthAnchor]);

  useEffect(() => {
    if (monthOptions.length === 0) return;
    if (!monthOptions.some((opt) => opt.value === selectedMonth)) {
      setSelectedMonth(monthOptions[0].value);
    }
  }, [monthOptions, selectedMonth]);

  const formatCurrencyCompact = (value: number) =>
    formatDisplayCurrencyCompact(value, displayCurrency);

  const monthData = useMemo(() => {
    const [year, month] = selectedMonth.split("-").map(Number);
    const monthPrefix = `${year}-${String(month).padStart(2, "0")}-`;
    const monthStart = new Date(year, month - 1, 1);
    const monthEnd = new Date(year, month, 0);
    monthStart.setHours(0, 0, 0, 0);
    monthEnd.setHours(23, 59, 59, 999);

    const tradeCountByDay = new Map<string, number>();
    const pnlByDay = new Map<string, number>();

    if (dailyActivityData?.daily_data_points?.length) {
      const points = dailyActivityData.daily_data_points;
      points.forEach((point) => {
        if (!point.date || !point.date.startsWith(monthPrefix)) return;
        tradeCountByDay.set(point.date, (tradeCountByDay.get(point.date) ?? 0) + (point.trade_count ?? 0));
        pnlByDay.set(point.date, (pnlByDay.get(point.date) ?? 0) + (point.total_pnl ?? 0));
      });
    }

    const firstDayOfWeek = new Date(year, month - 1, 1).getDay();
    const daysInMonth = monthEnd.getDate();
    const cells: DayCell[] = [];

    for (let i = 0; i < firstDayOfWeek; i++) {
      cells.push({ date: null, dateKey: "", dayOfMonth: null, tradeCount: 0, pnl: 0 });
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const d = new Date(year, month - 1, day);
      const key = formatLocalYmd(d);
      cells.push({
        date: d,
        dateKey: key,
        dayOfMonth: day,
        tradeCount: tradeCountByDay.get(key) ?? 0,
        pnl: pnlByDay.get(key) ?? 0,
      });
    }

    while (cells.length % 7 !== 0) {
      cells.push({ date: null, dateKey: "", dayOfMonth: null, tradeCount: 0, pnl: 0 });
    }

    const weeks: DayCell[][] = [];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

    const monthlyTradingDays = cells.filter((c) => c.dayOfMonth != null && c.tradeCount > 0).length;
    const monthlyTrades = cells.reduce((sum, c) => sum + (c.tradeCount || 0), 0);
    const monthlyPnl = cells.reduce((sum, c) => sum + (c.pnl || 0), 0);

    return { weeks, monthlyTradingDays, monthlyTrades, monthlyPnl };
  }, [dailyActivityData, selectedMonth]);

  const selectedDayLabel = useMemo(() => {
    if (!selectedDayKey) return "";
    try {
      const instant = startOfIanaCalendarDayUtc(selectedDayKey, effectiveTimeZone);
      return new Intl.DateTimeFormat("en-US", {
        timeZone: effectiveTimeZone,
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(instant);
    } catch {
      return selectedDayKey;
    }
  }, [selectedDayKey, effectiveTimeZone]);

  const handleOpenDayDetails = async (dateKey: string) => {
    if (!brokerAccountCode) return;
    setSelectedDayKey(dateKey);
    setIsDayModalOpen(true);
    setIsDayTradesLoading(true);
    setDayTradesError(null);
    try {
      const response = await fetchTrades({
        broker_account_code: brokerAccountCode,
        local_period: {
          local_start_date: dateKey,
          local_end_date: dateKey,
          ...(reportingTimeZone ? { timezone: reportingTimeZone } : {}),
        },
        limit: 500,
        offset: 0,
      });
      const converted = (response.trades || []).map(backendTradeToFrontendTrade);
      setDayTrades(converted);
    } catch (error) {
      setDayTrades([]);
      setDayTradesError(error instanceof Error ? error.message : "Failed to load day trades.");
    } finally {
      setIsDayTradesLoading(false);
    }
  };

  return (
    <Card
      data-statistics-shadcn-card
      data-dashboard-sidebar-surface-card=""
      data-trading-activity-calendar=""
      className={dashboardActivityCalendarCardClassName}
    >
      <CardHeader className={dashboardActivityCalendarHeaderClassName}>
        <div className="flex w-full flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center lg:justify-between lg:gap-x-4 lg:gap-y-3">
          <div className="flex items-center justify-between gap-3 lg:contents">
            <div className="flex min-w-0 flex-wrap items-baseline gap-2">
              <span data-dashboard-mini-stat-title className={titleClassName}>
                Trading Activity
              </span>
              {filterPeriodLabel ? (
                <span className={cn(periodLabelClassName, "hidden lg:inline")}>
                  {filterPeriodLabel}
                </span>
              ) : null}
            </div>
            <div
              data-trading-activity-calendar-month-select=""
              data-dashboard-header-field-select=""
              className="shrink-0 lg:order-3"
            >
              <FieldSelectFit
                filterChrome="sidebar"
                value={selectedMonth}
                onValueChange={setSelectedMonth}
                options={monthOptions}
                placeholder="Select month"
                fitToLongestOption
              />
            </div>
          </div>
          {filterPeriodLabel ? (
            <span className={cn(periodLabelClassName, "lg:hidden")}>{filterPeriodLabel}</span>
          ) : null}
          <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm leading-tight lg:order-2 lg:ml-auto lg:justify-end">
            <span
              data-dashboard-mini-stat-title
              className={cn(titleClassName, "whitespace-nowrap")}
            >
              Monthly Stats:
            </span>
            <span
              data-trade-log-pnl
              className={cn(metricClassName, "whitespace-nowrap")}
              style={{ color: pnlBarFill(monthData.monthlyPnl) }}
            >
              {formatCurrencyCompact(monthData.monthlyPnl)}
            </span>
            <span className={cn(metricClassName, "whitespace-nowrap", dashboardMutedTextClassName)}>
              {monthData.monthlyTradingDays}{" "}
              {monthData.monthlyTradingDays === 1 ? "day" : "days"} | {monthData.monthlyTrades} trades
            </span>
          </div>
        </div>
      </CardHeader>

      <CardContent className={dashboardActivityCalendarContentClassName}>
        <div className="h-full min-w-0 w-full max-w-full overflow-x-hidden">
          <div className="w-full min-w-0">
            <div className={cn("mb-1.5 sm:mb-2", calendarGridClassName)}>
              {DAYS.map((d, dayHeaderIdx) => (
                <div
                  key={d}
                  className={dayHeaderClassName}
                  data-trading-activity-calendar-cell=""
                  data-cell-tone="header"
                >
                  <span data-dashboard-mini-stat-title className={cn(titleClassName, "text-[10px] sm:text-xs")}>
                    <span className="lg:hidden">{DAYS_SHORT[dayHeaderIdx]}</span>
                    <span className="hidden lg:inline">{d}</span>
                  </span>
                </div>
              ))}
              <div
                className={cn(dayHeaderClassName, "hidden lg:block")}
                data-trading-activity-calendar-cell=""
                data-cell-tone="header"
              >
                <span data-dashboard-mini-stat-title className={cn(titleClassName, "text-xs")}>
                  Summary
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1.5 sm:gap-2">
              {monthData.weeks.map((week, idx) => {
                const activeDays = week.filter((d) => d.dayOfMonth != null && d.tradeCount > 0);
                const weekTrades = activeDays.reduce((sum, d) => sum + d.tradeCount, 0);
                const weekPnl = activeDays.reduce((sum, d) => sum + d.pnl, 0);
                return (
                  <div key={`week-${idx}`} className="flex min-w-0 flex-col gap-1 sm:gap-1.5 lg:gap-0">
                    <div className={calendarGridClassName}>
                      {week.map((day, dayIdx) => {
                        const hasData = day.dayOfMonth != null && day.tradeCount > 0;
                        const positive = day.pnl >= 0;
                        return (
                          <div
                            key={`${idx}-${dayIdx}`}
                            className={dayCellClassName(hasData, positive)}
                            data-trading-activity-calendar-cell=""
                            data-cell-tone={calendarDayCellTone(day, hasData, positive)}
                            onClick={hasData ? () => void handleOpenDayDetails(day.dateKey) : undefined}
                            onKeyDown={
                              hasData
                                ? (event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                      event.preventDefault();
                                      void handleOpenDayDetails(day.dateKey);
                                    }
                                  }
                                : undefined
                            }
                            role={hasData ? "button" : undefined}
                            tabIndex={hasData ? 0 : undefined}
                          >
                            {day.dayOfMonth == null ? null : (
                              <>
                                <div className="flex items-center justify-between gap-0.5">
                                  {hasData ? <CalendarTileIcon aria-hidden /> : <span />}
                                  <span
                                    className={cn(
                                      metricClassName,
                                      "text-[10px] leading-none sm:text-xs lg:text-sm",
                                      dashboardMutedTextClassName
                                    )}
                                  >
                                    {String(day.dayOfMonth).padStart(2, "0")}
                                  </span>
                                </div>
                                {hasData ? (
                                  <div className="min-w-0 self-end text-right">
                                    <span
                                      data-trade-log-pnl
                                      className={cn(
                                        metricClassName,
                                        "block max-w-full truncate text-[10px] leading-tight sm:text-sm md:text-base lg:text-xl"
                                      )}
                                      style={{ color: pnlBarFill(day.pnl) }}
                                    >
                                      {formatCurrencyCompact(day.pnl)}
                                    </span>
                                    <p
                                      className={cn(
                                        metricClassName,
                                        "mt-0.5 hidden text-[10px] sm:mt-1 sm:block sm:text-xs lg:text-sm",
                                        dashboardMutedTextClassName
                                      )}
                                    >
                                      {day.tradeCount} {day.tradeCount === 1 ? "trade" : "trades"}
                                    </p>
                                  </div>
                                ) : null}
                              </>
                            )}
                          </div>
                        );
                      })}

                      <div
                        className={cn(dayCellBaseClassName, "hidden bg-card lg:flex")}
                        data-trading-activity-calendar-cell=""
                        data-cell-tone="summary"
                      >
                        <span data-dashboard-mini-stat-title className={cn(titleClassName, "text-xs")}>
                          Week {idx + 1}
                        </span>
                        {activeDays.length > 0 ? (
                          <>
                            <span
                              data-trade-log-pnl
                              className={cn(metricClassName, "mt-2 block text-xl leading-tight")}
                              style={{ color: pnlBarFill(weekPnl) }}
                            >
                              {formatCurrencyCompact(weekPnl)}
                            </span>
                            <p className={cn(metricClassName, "mt-2", dashboardMutedTextClassName)}>
                              {activeDays.length} {activeDays.length === 1 ? "day" : "days"} | {weekTrades}{" "}
                              trades
                            </p>
                          </>
                        ) : (
                          <p className={cn(metricClassName, "mt-2 text-muted-foreground/80 dark:!text-white/80")}>
                            No activity
                          </p>
                        )}
                      </div>
                    </div>

                    <div
                      className={cn(
                        "flex min-w-0 items-center justify-between gap-2 rounded-md px-2 py-1.5 lg:hidden",
                        "bg-muted/40"
                      )}
                      data-trading-activity-calendar-cell=""
                      data-cell-tone="summary"
                    >
                      <span data-dashboard-mini-stat-title className={cn(titleClassName, "shrink-0 text-[10px] sm:text-xs")}>
                        Week {idx + 1}
                      </span>
                      {activeDays.length > 0 ? (
                        <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-0.5">
                          <span
                            data-trade-log-pnl
                            className={cn(metricClassName, "truncate text-xs sm:text-sm")}
                            style={{ color: pnlBarFill(weekPnl) }}
                          >
                            {formatCurrencyCompact(weekPnl)}
                          </span>
                          <span className={cn(metricClassName, "text-[10px] sm:text-xs", dashboardMutedTextClassName)}>
                            {activeDays.length}d · {weekTrades} trades
                          </span>
                        </div>
                      ) : (
                        <span className={cn(metricClassName, "text-[10px] text-muted-foreground/80 sm:text-xs dark:!text-white/80")}>
                          No activity
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CardContent>

      <TradingActivityDayModal
        isOpen={isDayModalOpen}
        onClose={() => setIsDayModalOpen(false)}
        selectedDayLabel={selectedDayLabel}
        isDayTradesLoading={isDayTradesLoading}
        dayTradesError={dayTradesError}
        dayTrades={dayTrades}
        brokerAccountCode={brokerAccountCode}
        effectiveTimeZone={effectiveTimeZone}
        dayCalendarKey={selectedDayKey}
      />
    </Card>
  );
};

export default TradeActivityCalendar;
